import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import { getFactoryDetail, getManagersForFactory } from '../../../data/internalDirectoryData';
import MIDataPanel from './MIDataPanel';
import RateEntryPanel from './RateEntryPanel';
import CurrentOffersPanel from './CurrentOffersPanel';

const Field = ({ label, children }) => (
  <div>
    <dt className="text-[11px] font-semibold uppercase tracking-wide" style={{ color: '#9ca3af' }}>{label}</dt>
    <dd className="text-sm mt-0.5" style={{ color: '#1f2937' }}>{children}</dd>
  </div>
);

const FactoryCard = ({ factory, autoExpand }) => {
  const navigate = useNavigate();
  const cardRef = useRef(null);
  const {
    millId,
    directoryFactoryId,
    name,
    plantName,
    state,
    district,
    operationalStatus,
    millCode,
    phone,
    email,
    miData,
  } = factory;

  // Click-to-expand full directory detail — separate toggle from Layer 3 (Update Rate); either can be
  // open independently. Reuses getFactoryDetail/getManagersForFactory from internalDirectoryData.js
  // (same functions factory-directory/index.jsx already uses) rather than reinventing the fetch.
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState(null);
  const [detail, setDetail] = useState(null);
  const [managers, setManagers] = useState([]);
  const [detailLoaded, setDetailLoaded] = useState(false);

  const handleToggleDetail = async () => {
    const next = !detailOpen;
    setDetailOpen(next);
    if (!next || detailLoaded) return; // only fetch once, on first expand

    if (!directoryFactoryId) {
      setDetailError('No directory record matched for this mill.');
      setDetailLoaded(true);
      return;
    }

    setDetailLoading(true);
    setDetailError(null);
    const [facRes, mgrRes] = await Promise.all([
      getFactoryDetail(directoryFactoryId),
      getManagersForFactory(directoryFactoryId),
    ]);
    if (facRes?.error) {
      setDetailError(facRes?.error?.message || 'Could not load factory detail.');
    } else {
      setDetail(facRes?.data);
    }
    setManagers(mgrRes?.data || []);
    setDetailLoading(false);
    setDetailLoaded(true);
  };

  // Deep-link target from My Submissions' Expired tab ("Update & Resubmit").
  useEffect(() => {
    if (!autoExpand) return;
    let attempts = 0;
    const tryScroll = () => {
      if (cardRef?.current) {
        cardRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } else if (attempts < 5) {
        attempts += 1;
        setTimeout(() => requestAnimationFrame(tryScroll), 150);
      }
    };
    requestAnimationFrame(tryScroll);
  }, [autoExpand]);

  return (
    <div
      ref={cardRef}
      className="bg-white rounded-2xl relative overflow-hidden"
      style={{
        border: autoExpand ? '1px solid #f59e0b' : '1px solid #e8ddd0',
        boxShadow: autoExpand ? '0 0 0 3px rgba(245,158,11,0.35)' : '0 2px 4px rgba(0,0,0,0.1)',
        borderTop: '3px solid #f59e0b',
      }}
    >
      {/* Share/Export PDF button — top right */}
      <button
        onClick={() => navigate(`/factory-rate-intelligence-center/share/${millId}`)}
        className="absolute top-4 right-4 w-8 h-8 rounded-full flex items-center justify-center transition hover:bg-amber-50 z-10"
        style={{ border: '1px solid #e8ddd0', background: '#fff' }}
        title="Share / Export as PDF"
      >
        <Icon name="Share2" size={14} color="#f59e0b" />
      </button>

      <div className="p-5 pr-14">
        {/* ── Layer 1: Identity ── */}
        <div>
          {/* Name + plant — name is clickable, toggles full directory detail */}
          <button
            onClick={handleToggleDetail}
            className="text-left w-full group"
            title="Click for full directory detail"
          >
            <h3
              className="text-lg font-bold leading-tight pr-2 flex items-center gap-1.5 group-hover:underline"
              style={{ color: '#1f2937', fontFamily: "'DM Sans', sans-serif" }}
            >
              {name}
              <Icon name={detailOpen ? 'ChevronUp' : 'ChevronDown'} size={15} color="#9ca3af" />
            </h3>
          </button>
          <p className="text-sm mt-0.5 mb-3" style={{ color: '#6b7280' }}>{plantName}</p>

          {/* Pill badges */}
          <div className="flex flex-wrap gap-2 mb-3">
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: '#fff7ed', color: '#c2410c', border: '1px solid #fed7aa' }}>
              {state}
            </span>
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: '#fef3c7', color: '#92400e', border: '1px solid #fde68a' }}>
              {district}
            </span>
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: '#dcfce7', color: '#16a34a', border: '1px solid #bbf7d0' }}>
              {operationalStatus}
            </span>
            <span className="px-2.5 py-1 rounded-full text-xs font-semibold" style={{ background: '#dbeafe', color: '#1e40af', border: '1px solid #bfdbfe' }}>
              {millCode}
            </span>
          </div>

          {/* Contact chips */}
          <div className="flex flex-wrap gap-2">
            <a
              href={`tel:${phone}`}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition hover:bg-amber-50"
              style={{ border: '1px solid #e8ddd0', color: '#1f2937' }}
            >
              <Icon name="Phone" size={12} color="#f59e0b" />
              {phone}
            </a>
            <a
              href={`mailto:${email}`}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition hover:bg-amber-50"
              style={{ border: '1px solid #e8ddd0', color: '#1f2937' }}
            >
              <Icon name="Mail" size={12} color="#f59e0b" />
              {email}
            </a>
          </div>

          {/* Full directory detail — click the name to toggle */}
          {detailOpen && (
            <div className="mt-3 rounded-xl p-4" style={{ background: '#f8f1e9', border: '1px solid #e8ddd0' }}>
              {detailLoading && (
                <p className="text-xs" style={{ color: '#6b7280' }}>Loading directory detail…</p>
              )}
              {!detailLoading && detailError && (
                <p className="text-xs" style={{ color: '#DC143C' }}>{detailError}</p>
              )}
              {!detailLoading && !detailError && detail && (
                <div className="space-y-3">
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <Field label="Address">{detail?.address || '—'}</Field>
                    {detail?.website && (
                      <Field label="Website">
                        <a href={detail?.website} target="_blank" rel="noopener noreferrer" className="hover:underline" style={{ color: '#d97706' }}>
                          {detail?.website}
                        </a>
                      </Field>
                    )}
                  </dl>

                  <div>
                    <p className="text-[11px] font-semibold uppercase tracking-wide mb-2" style={{ color: '#9ca3af' }}>
                      Managers / Contacts
                    </p>
                    {managers?.length === 0 ? (
                      <p className="text-xs" style={{ color: '#6b7280' }}>No contacts listed.</p>
                    ) : (
                      <ul className="space-y-2">
                        {managers?.map((m) => (
                          <li key={m?.id} className="rounded-lg p-2.5" style={{ background: '#fff', border: '1px solid #e8ddd0' }}>
                            <p className="text-sm font-semibold" style={{ color: '#1f2937' }}>{m?.managerName}</p>
                            {m?.designation && <p className="text-xs" style={{ color: '#6b7280' }}>{m?.designation}</p>}
                            <div className="flex flex-wrap gap-3 mt-1.5">
                              {m?.mobile && (
                                <a href={`tel:${m?.mobile}`} className="flex items-center gap-1 text-xs font-medium hover:underline" style={{ color: '#d97706' }}>
                                  <Icon name="Smartphone" size={11} /> {m?.mobile}
                                </a>
                              )}
                              {m?.phone && m?.phone !== m?.mobile && (
                                <a href={`tel:${m?.phone}`} className="flex items-center gap-1 text-xs font-medium hover:underline" style={{ color: '#d97706' }}>
                                  <Icon name="Phone" size={11} /> {m?.phone}
                                </a>
                              )}
                              {m?.email && (
                                <a href={`mailto:${m?.email}`} className="flex items-center gap-1 text-xs font-medium hover:underline" style={{ color: '#16a34a' }}>
                                  <Icon name="Mail" size={11} /> {m?.email}
                                </a>
                              )}
                            </div>
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* ── Layer 2: Market Intelligence ── */}
        <MIDataPanel data={miData} />

        {/* ── Current Offers — every live grade for this mill, Live or Expired, with a
            Withdraw action right here so a stale sibling grade isn't only discoverable
            later on My Submissions' Expired tab. ── */}
        <CurrentOffersPanel millId={millId} />

        {/* ── Layer 3: Rate Entry (collapsible) ── */}
        <RateEntryPanel millId={millId} millName={name} autoOpen={autoExpand} />
      </div>
    </div>
  );
};

export default FactoryCard;
